import axios from 'axios';
import { CATALOG_BASE_URL } from '../constants';

/** What an upload-and-download automation returns: the generated file plus its summary. */
export interface FileResult<S> {
  blob: Blob;
  filename: string;
  summary: S | null;
}

/** Reads the filename from a `Content-Disposition` header, or returns `fallback`. */
export function parseFilename(contentDisposition: string | null, fallback: string): string {
  if (!contentDisposition) return fallback;
  const match = contentDisposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  return match ? decodeURIComponent(match[1]) : fallback;
}

/** Saves a blob in the browser as `filename`. */
export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Turns an axios error into the backend's `detail` message. Works for blob-typed requests
 * (the JSON error body arrives as a Blob) as well as ordinary JSON responses.
 */
export async function toApiError(error: unknown, fallback: string): Promise<Error> {
  if (!axios.isAxiosError(error)) return error instanceof Error ? error : new Error(fallback);
  const body = error.response?.data;
  if (body instanceof Blob && String(error.response?.headers?.['content-type'] || '').includes('application/json')) {
    try {
      const data = JSON.parse(await body.text());
      if (typeof data.detail === 'string') return new Error(data.detail);
    } catch {
      // fall through to the generic message
    }
  } else if (body && typeof body === 'object' && typeof (body as { detail?: unknown }).detail === 'string') {
    return new Error((body as { detail: string }).detail);
  }
  if (!error.response) return new Error('Could not reach the backend. Is it running?');
  return new Error(`${fallback} (status ${error.response.status})`);
}

/** POSTs a multipart form and returns the generated file plus its `X-Summary` header. */
export async function postForFile<S>(
  path: string,
  form: FormData,
  fallbackName: string,
  failure: string,
): Promise<FileResult<S>> {
  try {
    const response = await axios.post(`${CATALOG_BASE_URL}${path}`, form, { responseType: 'blob' });
    const summaryHeader = response.headers['x-summary'];
    return {
      blob: response.data as Blob,
      filename: parseFilename(response.headers['content-disposition'] || null, fallbackName),
      summary: summaryHeader ? (JSON.parse(summaryHeader) as S) : null,
    };
  } catch (error) {
    throw await toApiError(error, failure);
  }
}

export async function getJson<T>(path: string, failure: string): Promise<T> {
  try {
    return (await axios.get<T>(`${CATALOG_BASE_URL}${path}`)).data;
  } catch (error) {
    throw await toApiError(error, failure);
  }
}

export async function postJson<T>(path: string, form: FormData, failure: string): Promise<T> {
  try {
    return (await axios.post<T>(`${CATALOG_BASE_URL}${path}`, form)).data;
  } catch (error) {
    throw await toApiError(error, failure);
  }
}
